import { useHomepageHydration } from '../context/HomepageRenderContext'
import { MemoryRouter } from 'react-router-dom'
import { lazyWithStyles } from "../utils/lazyWithStyles";
import { roomCancellationEnabled } from "../utils/roomCancellationPublic";
import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType, useParams } from 'react-router-dom'
import { HomeLandingFrame } from './HomeLandingFrame'
import RouteScrollRestoration from '../Components/Navigation/RouteScrollRestoration'
import PublicRoutePrefetcher from '../Components/Navigation/PublicRoutePrefetcher'
import HeaderThree from '../Components/Header/HeaderThree'
import ScrollToTop from '../Components/ScrollToTop'
import CartBootstrapper from '../Components/CartBootstrapper'
import AffiliateAttributionBootstrapper from '../Components/AffiliateAttributionBootstrapper'
import BackNavigationGuard from '../Components/Navigation/BackNavigationGuard'
import TestSiteAccessGate from '../Components/TestSiteAccessGate'
import TestEnvironmentOverlay from '../Components/TestEnvironmentOverlay'
import UserBookingsBootstrapper from '../Components/UserBookingsBootstrapper'
import { SupportDeskProvider } from '../context/SupportDeskContext'
import { WebsitePromotionsProvider } from '../context/WebsitePromotionsContext'
import { WebsiteConfigProvider, useWebsiteConfig } from '../context/WebsiteConfigContext'
import { QueryProvider } from '../context/QueryContext'
import { isAffiliateAttributionEnabled } from '../utils/affiliateRollout'
import { buildAdminAppUrl } from '../utils/adminAppLinks'
import { getOrCreateVisitorId } from '../utils/portalVisitor'
import { trackPortalVisitor } from '../api/visitorApi'
import { isPuriDemoEnabled, isPuriDemoPath } from '../utils/demoRoutes'
import { HOME_LANDING_ROUTES, isHomeLandingPath, resolveHomeLandingModeFromPath } from '../utils/homeLandingRoutes'
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion'
import { recordPerformanceNavigation } from '../utils/performanceMonitoring'
import { beginRouteFamilyNavigation, recordRouteFamilyMetric, resolveRouteFamily } from '../utils/routeFamilyMonitoring'
import { isDetailPerformanceRoute } from '../utils/detailRouteBootstrap'
import { isModernStyleRoute } from '../utils/modernStyleRoutes'
import DetailFirstPaintShell from './Explore/DetailFirstPaintShell'
import { PublicListingImageBudgetProvider } from '../Components/Image'
import { resolvePublicListingFamily } from '../utils/publicListingContracts'
import { isSupportEntryEnabled } from '../utils/supportRollout'
import { resolveAssistanceSurfaces } from '../utils/assistanceHub'
import QuickBookRouteEntry from '../Components/QuickBook/QuickBookRouteEntry'

const HomeLanding = lazyWithStyles(() => import('./HomeLanding'))
const HomeOne = lazyWithStyles(() => import('./HomeOne'))
const ExploreHomeLanding = lazyWithStyles(() => import('./Explore/ExploreHomeLanding'))
const HomeNoticeSlideUp = lazyWithStyles(() => import('../Components/Notices/HomeNoticeSlideUp'))
const Footer = lazyWithStyles(() => import('../Components/Footer/Footer'))
const ExploreQuickRoutesBar = lazyWithStyles(() => import('../Components/ExploreQuickRoutesBar'))
const WebsitePromotionsViewport = lazyWithStyles(() => import('../Components/Promotions/WebsitePromotionsViewport'))
const SupportDeskDrawer = lazyWithStyles(() => import('../Components/Support/SupportDeskDrawer'))
const AI_CONCIERGE_ENABLED = process.env.REACT_APP_AI_CONCIERGE_ENABLED === 'true'
const AI_CONCIERGE_HELP_HUB_ENABLED = process.env.REACT_APP_AI_CONCIERGE_HELP_HUB_ENABLED === 'true'
const EcoTourConcierge = AI_CONCIERGE_ENABLED
  ? lazyWithStyles(() => import('../Components/Concierge/EcoTourConcierge'))
  : null
const Destination = lazyWithStyles(() => import('./Destination'))
const DestinationDetails = lazyWithStyles(() => import('./DestinationDetails'))
const Payment = lazyWithStyles(() => import('./Payment'))
const RoomCollectionPayment = lazyWithStyles(() => import('./RoomCollectionPayment'))
const TerminalCartPayment = lazyWithStyles(() => import('./TerminalCartPayment'))
const AccountLayout = lazyWithStyles(() => import('./Account/AccountLayout'))
const AccountBookings = lazyWithStyles(() => import('./Account/AccountBookings'))
const AccountPackageBookings = lazyWithStyles(() => import('./Account/AccountPackageBookings'))
const AccountPackageBookingDetail = lazyWithStyles(() => import('./Account/AccountPackageBookingDetail'))
const AccountProfile = lazyWithStyles(() => import('./Account/AccountProfile'))
const AccountReceipt = lazyWithStyles(() => import('./Account/AccountReceipt'))
const RoomCancellations = lazyWithStyles(() => import('./Account/room-cancellations/RoomCancellations'))
const AccountCartSummary = lazyWithStyles(() => import('./Account/AccountCartSummary'))
const AccountQueries = lazyWithStyles(() => import('./Account/AccountQueries'))
const AccountWishlist = lazyWithStyles(() => import('./Account/AccountWishlist'))
const AccountCart = lazyWithStyles(() => import('./Account/AccountCart'))
const AccountGuests = lazyWithStyles(() => import('./Account/AccountGuests'))
const AccountRewards = lazyWithStyles(() => import('./Account/AccountRewards'))
const SupportQueries = lazyWithStyles(() => import('./SupportQueries'))
const ExploreLanding = lazyWithStyles(() => import('./Explore/ExploreLanding'))
const ExploreDistrictsLanding = lazyWithStyles(() => import('./Explore/ExploreDistrictsLanding'))
const ExploreDistrict = lazyWithStyles(() => import('./Explore/ExploreDistrict'))
const ExploreCampRoute = lazyWithStyles(() => import('./Explore/ExploreCampRoute'))
const ExploreJungleSafari = lazyWithStyles(() => import('./Explore/ExploreJungleSafari'))
const ExploreCruiseBoating = lazyWithStyles(() => import('./Explore/ExploreCruiseBoating'))
const ExploreJungleSafariLanding = lazyWithStyles(() => import('./Explore/ExploreJungleSafariLanding'))
const ExploreCruiseBoatingLanding = lazyWithStyles(() => import('./Explore/ExploreCruiseBoatingLanding'))
const ExploreExperienceLanding = lazyWithStyles(() => import('./Explore/ExploreExperienceLanding'))
const ExploreExperienceDetail = lazyWithStyles(() => import('./Explore/ExploreExperienceDetail'))
const ExploreTrekTrail = lazyWithStyles(() => import('./Explore/ExploreTrekTrail'))
const ExploreTrekTrailLanding = lazyWithStyles(() => import('./Explore/ExploreTrekTrailLanding'))
const PackageListingPage = lazyWithStyles(() => import('./Packages/PackageListingPage'))
const PackageDetailPage = lazyWithStyles(() => import('./Packages/PackageDetailRoute'))
const ItineraryPlanner = lazyWithStyles(() => import('./ItineraryPlanner'))
const TourOperators = lazyWithStyles(() => import('./TourOperators'))
const HostedFormPage = lazyWithStyles(() => import('./HostedFormPage'))
const PrivacyPolicy = lazyWithStyles(() => import('./Legal/PrivacyPolicy'))
const OpsBridgePrivacyPolicy = lazyWithStyles(() => import('./Legal/OpsBridgePrivacyPolicy'))
const OpsBridgeDownloadPage = lazyWithStyles(() => import('./OpsBridge/OpsBridgeDownloadPage'))
const OpsBridgeDownloadRedirect = lazyWithStyles(() => import('./OpsBridge/OpsBridgeDownloadPage').then((module) => ({ default: module.OpsBridgeDownloadRedirect })))
const RefundPolicy = lazyWithStyles(() => import('./Legal/RefundPolicy'))
const TermsAndConditions = lazyWithStyles(() => import('./Legal/TermsAndConditions'))
const WebsiteContentRoute = lazyWithStyles(() => import('./WebsiteContent/WebsiteContentRoute'))
const WebsiteContentPreviewPage = lazyWithStyles(() => import('./WebsiteContent/WebsiteContentPreviewPage'))
const PuriDemoHome = lazyWithStyles(() => import('./DemoPuri/PuriDemoHome'))
const PuriDemoDetail = lazyWithStyles(() => import('./DemoPuri/PuriDemoDetail'))

const EXPLORE_HOME_HEADER_PROPS = { enableLandingNav: true, landingMode: 'explore' }
const HOME_ROUTE_ORDER = ['stays', 'experiences', 'explore']
const HOME_ROUTE_TRANSITION_MS = 720

export function RouteLoadingFallback({ pathname = '' }) {
  if (isDetailPerformanceRoute(pathname)) {
    const segments = pathname.split('/').filter(Boolean)
    const kind = pathname.startsWith('/stays-camps/')
      ? 'camp'
      : pathname.startsWith('/experiences/') || pathname.startsWith('/jungle-safaris/') || pathname.startsWith('/cruise-boating/') || pathname.startsWith('/treks-trails/') || pathname.startsWith('/track-trails/')
        ? 'experience'
        : 'destination'
    return <DetailFirstPaintShell kind={kind} title={segments[segments.length - 1] || ''} />
  }
  return (
    <div className="ecotour-route-loading" role="status" aria-live="polite" aria-atomic="true">
      <span className="ecotour-route-loading__mark" aria-hidden="true">
        <i className="fa-solid fa-leaf" />
      </span>
      <span>Loading this page…</span>
    </div>
  )
}

function useDeferredPortalUi() {
  const [ready, setReady] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    let delayHandle
    let idleHandle

    const reveal = () => {
      if (!cancelled) React.startTransition(() => setReady(true))
    }
    const schedule = () => {
      delayHandle = window.setTimeout(() => {
        if (typeof window.requestIdleCallback === 'function') {
          idleHandle = window.requestIdleCallback(reveal, { timeout: 2000 })
        } else {
          reveal()
        }
      }, 1200)
    }

    // Secondary controls must not wait for slow/unavailable gallery images.
    schedule()

    return () => {
      cancelled = true
      if (delayHandle) window.clearTimeout(delayHandle)
      if (idleHandle && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle)
      }
    }
  }, [])

  return ready
}

function useDeferredDetailGlobals(pathname) {
  const shouldDefer = isDetailPerformanceRoute(pathname)
  const activatedRef = React.useRef(!shouldDefer)
  const [activated, setActivated] = React.useState(() => !shouldDefer)

  React.useEffect(() => {
    if (!shouldDefer || activatedRef.current) {
      activatedRef.current = true
      setActivated(true)
      return undefined
    }

    const activate = () => {
      activatedRef.current = true
      setActivated(true)
    }
    const passiveOptions = { capture: true, passive: true }
    const keyOptions = { capture: true }

    window.addEventListener('pointerdown', activate, passiveOptions)
    window.addEventListener('touchstart', activate, passiveOptions)
    window.addEventListener('wheel', activate, passiveOptions)
    window.addEventListener('keydown', activate, keyOptions)

    return () => {
      window.removeEventListener('pointerdown', activate, passiveOptions)
      window.removeEventListener('touchstart', activate, passiveOptions)
      window.removeEventListener('wheel', activate, passiveOptions)
      window.removeEventListener('keydown', activate, keyOptions)
    }
  }, [shouldDefer])

  return activated
}

function LegacyParamRedirect({ to }) {
  const params = useParams()
  return <Navigate to={to(params)} replace />
}

function AdminAppRedirect({ to }) {
  const location = useLocation()
  const params = useParams()

  React.useEffect(() => {
    const path = typeof to === 'function' ? to(params) : to
    window.location.replace(buildAdminAppUrl(`${path}${location.search || ''}`))
  }, [location.search, params, to])

  return null
}

function PortalVisitorTracker({ enabled = true }) {
  const location = useLocation()

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined
    const visitorId = getOrCreateVisitorId()
    if (!visitorId) return
    const pagePath = `${location.pathname || '/'}${location.search || ''}`
    const referrer = document?.referrer || null
    trackPortalVisitor({ visitorId, pagePath, referrer }).catch(() => {})
  }, [enabled, location.pathname, location.search])

  return null
}

function PortalPerformanceMonitor() {
  const location = useLocation()
  const navigationType = useNavigationType()

  React.useEffect(() => {
    recordPerformanceNavigation(location.pathname || '/')
    const descriptor = beginRouteFamilyNavigation(location.pathname || '/', { navigationType })
    if (!descriptor.monitored) return undefined
    if (!["home_stays", "home_experiences", "home_explore", "district"].includes(descriptor.routeFamily)) {
      return undefined
    }
    let cancelled = false
    let attempts = 0
    const checkShell = () => {
      if (cancelled) return
      attempts += 1
      const main = document.getElementById('main-content')
      const routeLoading = main?.querySelector('.ecotour-route-loading')
      const meaningful = (main?.textContent || '').replace(/\s+/g, ' ').trim().length >= 24
      if (main && !routeLoading && meaningful) {
        recordRouteFamilyMetric('compact_ready', {
          segment: 'route-shell',
          inventoryState: 'unknown',
        })
        window.setTimeout(() => {
          if (!cancelled && resolveRouteFamily(window.location.pathname).pathname === descriptor.pathname) {
            recordRouteFamilyMetric('deferred_ready', {
              segment: 'route-shell',
              inventoryState: 'unknown',
            })
          }
        }, 2000)
        return
      }
      if (attempts < 300) window.requestAnimationFrame(checkShell)
      else recordRouteFamilyMetric('compact_failed', {
        segment: 'route-shell',
        inventoryState: 'failed',
      })
    }
    const frame = window.requestAnimationFrame(checkShell)
    return () => {
      cancelled = true
      window.cancelAnimationFrame(frame)
    }
  }, [location.pathname, navigationType])

  return null
}

function LegacyStyleRouteBootstrapper() {
  const location = useLocation()

  React.useLayoutEffect(() => {
    if (isModernStyleRoute(location.pathname)) {
      window.__disableEcoTourLegacyStyles?.('modern-route')
      return
    }
    window.__loadEcoTourLegacyStyles?.('route-change')
  }, [location.pathname])

  return null
}

function HomeRouteContent({ mode }) {
  const hydrating = useHomepageHydration()
  return (
    <main className="min-h-[70vh] pb-12">
      <HomeLandingFrame explore={mode === 'explore'}>
        {hydrating ? <div style={{ minHeight: 480 }} role="status"><span className="sr-only">Loading {mode === 'experiences' ? 'experiences' : 'stays'}…</span></div> : (
          <React.Suspense fallback={<div style={{ minHeight: 480 }} role="status"><span className="sr-only">Loading {mode === 'experiences' ? 'experiences' : 'stays'}…</span></div>}>
            {mode === 'explore' ? <ExploreHomeLanding embedded showHeader={false} /> : <HomeLanding embedded landingMode={mode === 'experiences' ? 'experiences' : 'stays'} />}
          </React.Suspense>
        )}
      </HomeLandingFrame>
    </main>
  )
}

function HomeLandingRouteCarousel({ mode, deferredUiReady = false }) {
  const containerRef = React.useRef(null)
  const activeModeRef = React.useRef(mode)
  const prefersReducedMotion = usePrefersReducedMotion()
  const [minHeight, setMinHeight] = React.useState(0)
  const [transition, setTransition] = React.useState(() => ({
    activeMode: mode,
    leavingMode: '',
    direction: 'none',
    animating: false,
  }))

  React.useLayoutEffect(() => {
    if (!mode) return
    const currentMode = activeModeRef.current
    if (currentMode === mode) return

    activeModeRef.current = mode
    if (prefersReducedMotion) {
      setMinHeight(0)
      setTransition({
        activeMode: mode,
        leavingMode: '',
        direction: 'none',
        animating: false,
      })
      return
    }

    const currentIndex = HOME_ROUTE_ORDER.indexOf(currentMode)
    const nextIndex = HOME_ROUTE_ORDER.indexOf(mode)
    const direction = currentIndex >= 0 && nextIndex >= 0 && nextIndex < currentIndex ? 'back' : 'forward'
    const measuredHeight = containerRef.current?.getBoundingClientRect?.().height || 0

    setMinHeight((currentHeight) => (currentHeight === measuredHeight ? currentHeight : measuredHeight))
    setTransition((current) => {
      if (
        current.activeMode === mode &&
        current.leavingMode === currentMode &&
        current.direction === direction &&
        current.animating
      ) {
        return current
      }
      return {
        activeMode: mode,
        leavingMode: currentMode,
        direction,
        animating: true,
      }
    })
  }, [mode, prefersReducedMotion])

  React.useEffect(() => {
    if (!transition.animating) return undefined
    const timer = window.setTimeout(() => {
      setTransition((current) => ({
        ...current,
        leavingMode: '',
        direction: 'none',
        animating: false,
      }))
      setMinHeight(0)
    }, HOME_ROUTE_TRANSITION_MS)

    return () => window.clearTimeout(timer)
  }, [transition.animating])

  const renderPanel = (contentMode, panelState) => {
    if (!contentMode) return null
    const isActive = panelState === 'active'
    return (
      <div
        key={contentMode}
        className={[
          'ecotour-home-route-panel',
          isActive ? 'is-active' : 'is-leaving',
          transition.animating && transition.direction !== 'none' ? `is-${transition.direction}` : '',
        ].join(' ')}
        aria-hidden={isActive ? undefined : 'true'}
        inert={isActive ? undefined : ''}
      >
        <HomeRouteContent mode={contentMode} />
      </div>
    )
  }

  return (
    <div className="ecotour-home-layout">
      <HeaderThree
        initialAppearance="overlay"
        enableLandingNav
        landingMode={mode}
        syncLandingSearchMode
      />
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {mode === 'explore' ? 'Explore' : mode === 'experiences' ? 'Experiences' : 'Stays'} page loaded
      </p>
      <div
        id="home-landing-content"
        ref={containerRef}
        className={`ecotour-home-route-carousel ${transition.animating ? 'is-animating' : ''}`}
        style={minHeight ? { minHeight: `${minHeight}px` } : undefined}
      >
        {transition.leavingMode ? renderPanel(transition.leavingMode, 'leaving') : null}
        {renderPanel(transition.activeMode || mode, 'active')}
      </div>
      {deferredUiReady ? (
        <React.Suspense fallback={null}>
          <HomeNoticeSlideUp />
        </React.Suspense>
      ) : null}
      <ScrollToTop />
    </div>
  )
}

function PortalRoutes({ deferredUiReady = false }) {
  const puriDemoEnabled = isPuriDemoEnabled()
  const location = useLocation()
  const homeMode = resolveHomeLandingModeFromPath(location.pathname)

  if (homeMode) {
    return <HomeLandingRouteCarousel mode={homeMode} deferredUiReady={deferredUiReady} />
  }

  return (
    <Routes>
      <Route path="/" element={<HomeOne landingMode="stays" />}></Route>
      <Route path={HOME_LANDING_ROUTES.stays} element={<HomeOne landingMode="stays" />} />
      <Route path={HOME_LANDING_ROUTES.experiences} element={<HomeOne landingMode="experiences" />} />
      <Route path={HOME_LANDING_ROUTES.explore} element={<ExploreHomeLanding headerProps={EXPLORE_HOME_HEADER_PROPS} />} />
      <Route path="/demo/puri" element={puriDemoEnabled ? <PuriDemoHome /> : <Navigate to="/" replace />} />
      <Route
        path="/demo/puri/:section/:itemSlug"
        element={puriDemoEnabled ? <PuriDemoDetail /> : <Navigate to="/" replace />}
      />
      <Route path="/destination" element={<Navigate to="/destinations" replace />} />
      <Route path="/destinations" element={<Destination />}></Route>
      <Route path="/destination/:destinationSlug" element={<DestinationDetails />} />
      <Route path="/checkout/:experienceType" element={<Navigate to="/account/cart" replace />} />
      <Route path="/checkout/:experienceType/result/:status" element={<Navigate to="/account/cart" replace />} />
      <Route path="/payment" element={<Payment />} />
      <Route path="/payment/room-collection" element={<RoomCollectionPayment />} />
      <Route path="/payment/cart-checkout" element={<TerminalCartPayment />} />
      <Route path="/account" element={<AccountLayout />}>
        <Route index element={<Navigate to="/account/bookings/stays" replace />} />
        <Route path="bookings" element={<Navigate to="/account/bookings/stays" replace />} />
        <Route path="bookings/stays" element={<AccountBookings />} />
        <Route path="bookings/experiences" element={<AccountBookings />} />
        <Route path="bookings/packages" element={<AccountPackageBookings />} />
        <Route path="bookings/packages/:packageRef" element={<AccountPackageBookingDetail />} />
        <Route path="profile" element={<AccountProfile />} />
        <Route path="queries" element={<AccountQueries />} />
        <Route path="wishlist" element={<AccountWishlist />} />
        <Route path="cart" element={<AccountCart />} />
        <Route path="rewards" element={<AccountRewards />} />
        <Route path="guests" element={<AccountGuests />} />
        <Route path="receipt/:bookingId" element={<AccountReceipt />} />
        {roomCancellationEnabled() && <Route path="room-cancellations" element={<RoomCancellations />} />}
        <Route path="cart-summary/:referenceCode" element={<AccountCartSummary />} />
      </Route>
      <Route path="/support/queries" element={<SupportQueries />} />
      <Route path="/tour-operators" element={<TourOperators />} />
      <Route path="/outreach-partners/apply" element={<AdminAppRedirect to="/outreach-partners/apply" />} />
      <Route path="/outreach-partners/login" element={<AdminAppRedirect to="/outreach-partners/login" />} />
      <Route path="/outreach-partners/forgot-password" element={<AdminAppRedirect to="/outreach-partners/forgot-password" />} />
      <Route path="/outreach-partners/reset-password/:token" element={<AdminAppRedirect to={({ token }) => `/outreach-partners/reset-password/${token}`} />} />
      <Route path="/outreach-partners/invite/:token" element={<AdminAppRedirect to={({ token }) => `/outreach-partners/invite/${token}`} />} />
      <Route path="/affiliate/apply" element={<AdminAppRedirect to="/outreach-partners/apply" />} />
      <Route path="/affiliate/login" element={<AdminAppRedirect to="/outreach-partners/login" />} />
      <Route path="/affiliate/forgot-password" element={<AdminAppRedirect to="/outreach-partners/forgot-password" />} />
      <Route path="/affiliate/reset-password/:token" element={<AdminAppRedirect to={({ token }) => `/outreach-partners/reset-password/${token}`} />} />
      <Route path="/affiliate/invite/:token" element={<AdminAppRedirect to={({ token }) => `/outreach-partners/invite/${token}`} />} />
      <Route path="/stays-camps" element={<ExploreLanding />} />
      <Route path="/stays-camps/:destinationSlug/:campSlug" element={<ExploreCampRoute />} />
      <Route path="/districts" element={<ExploreDistrictsLanding />} />
      <Route path="/district/:districtSlug" element={<ExploreDistrict />} />
      <Route path="/experiences" element={<ExploreExperienceLanding />} />
      <Route path="/experiences/:destinationSlug/:safariSlug" element={<ExploreJungleSafari />} />
      <Route path="/jungle-safaris" element={<ExploreJungleSafariLanding />} />
      <Route path="/jungle-safaris/:destinationSlug/:safariSlug" element={<ExploreJungleSafari />} />
      <Route path="/cruise-boating" element={<ExploreCruiseBoatingLanding />} />
      <Route path="/cruise-boating/:destinationSlug/:cruiseSlug" element={<ExploreCruiseBoating />} />
      <Route path="/packages" element={<PackageListingPage />} />
      <Route path="/packages/:slug" element={<PackageDetailPage />} />
      <Route path="/treks-trails" element={<ExploreTrekTrailLanding />} />
      <Route path="/track-trails" element={<Navigate to="/treks-trails" replace />} />
      <Route path="/treks-trails/:trekSlug" element={<ExploreTrekTrail />} />
      <Route
        path="/track-trails/:trekSlug"
        element={<LegacyParamRedirect to={({ trekSlug }) => `/treks-trails/${trekSlug}`} />}
      />
      <Route
        path="/treks-trails/:destinationSlug/:trekSlug"
        element={<ExploreTrekTrail />}
      />
      <Route
        path="/track-trails/:destinationSlug/:trekSlug"
        element={<LegacyParamRedirect to={({ destinationSlug, trekSlug }) => `/treks-trails/${destinationSlug}/${trekSlug}`} />}
      />
      <Route path="/explore" element={<Navigate to={HOME_LANDING_ROUTES.explore} replace />} />
      <Route path="/explore/districts" element={<Navigate to="/districts" replace />} />
      <Route
        path="/explore/district/:districtSlug"
        element={<LegacyParamRedirect to={({ districtSlug }) => `/district/${districtSlug}`} />}
      />
      <Route
        path="/explore/destination/:destinationSlug"
        element={<LegacyParamRedirect to={({ destinationSlug }) => `/destination/${destinationSlug}`} />}
      />
      <Route
        path="/explore/camp/:destinationSlug/:campSlug"
        element={<LegacyParamRedirect to={({ destinationSlug, campSlug }) => `/stays-camps/${destinationSlug}/${campSlug}`} />}
      />
      <Route path="/explore/experiences" element={<Navigate to="/experiences" replace />} />
      <Route
        path="/explore/experiences/:destinationSlug/:safariSlug"
        element={<LegacyParamRedirect to={({ destinationSlug, safariSlug }) => `/experiences/${destinationSlug}/${safariSlug}`} />}
      />
      <Route path="/_preview/content/:token" element={<WebsiteContentPreviewPage />} />
      <Route path="/opsbridge" element={<Navigate to="/ecotour-admin" replace />} />
      <Route path="/opsbridge/download" element={<OpsBridgeDownloadRedirect />} />
      <Route path="/opsbridge/download/:versionCode" element={<OpsBridgeDownloadRedirect />} />
      <Route path="/ecotour-admin" element={<OpsBridgeDownloadPage />} />
      <Route path="/opsbridg" element={<Navigate to="/ecotour-admin" replace />} />
      <Route path="/ecotour-admin/download" element={<OpsBridgeDownloadRedirect />} />
      <Route path="/ecotour-admin/download/:versionCode" element={<OpsBridgeDownloadRedirect />} />
      <Route path="/:contentSegmentOne" element={<WebsiteContentRoute />} />
      <Route path="/:categorySlug/:experienceSlug" element={<WebsiteContentRoute fallbackElement={<ExploreExperienceDetail />} />} />
      <Route path="/:contentSegmentOne/:contentSegmentTwo/:contentSegmentThree" element={<WebsiteContentRoute />} />
      <Route path="/:contentSegmentOne/:contentSegmentTwo/:contentSegmentThree/:contentSegmentFour" element={<WebsiteContentRoute />} />
      <Route path="/explore/jungle-safari" element={<Navigate to="/jungle-safaris" replace />} />
      <Route
        path="/explore/jungle-safari/:destinationSlug/:safariSlug"
        element={<LegacyParamRedirect to={({ destinationSlug, safariSlug }) => `/jungle-safaris/${destinationSlug}/${safariSlug}`} />}
      />
      <Route path="/explore/cruise-boating" element={<Navigate to="/cruise-boating" replace />} />
      <Route
        path="/explore/cruise-boating/:destinationSlug/:cruiseSlug"
        element={<LegacyParamRedirect to={({ destinationSlug, cruiseSlug }) => `/cruise-boating/${destinationSlug}/${cruiseSlug}`} />}
      />
      <Route path="/forms/:slug" element={<HostedFormPage />} />
      <Route path="/embed/forms/:slug" element={<HostedFormPage embedMode />} />
      <Route path="/explore/treks-trails" element={<Navigate to="/treks-trails" replace />} />
      <Route path="/explore/track-trails" element={<Navigate to="/treks-trails" replace />} />
      <Route
        path="/explore/treks-trails/:trekSlug"
        element={<LegacyParamRedirect to={({ trekSlug }) => `/treks-trails/${trekSlug}`} />}
      />
      <Route
        path="/explore/treks-trails/:destinationSlug/:trekSlug"
        element={<LegacyParamRedirect to={({ destinationSlug, trekSlug }) => `/treks-trails/${destinationSlug}/${trekSlug}`} />}
      />
      <Route
        path="/explore/track-trails/:destinationSlug/:trekSlug"
        element={<LegacyParamRedirect to={({ destinationSlug, trekSlug }) => `/treks-trails/${destinationSlug}/${trekSlug}`} />}
      />
      <Route path="/itinerary-planner" element={<ItineraryPlanner />} />
      <Route path="/itinerary-planner/share/:shareSlug" element={<ItineraryPlanner />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/policies/ecotour-admin-privacy-policy" element={<OpsBridgePrivacyPolicy />} />
      <Route path="/policies/ops-bridge-privacy-policy" element={<Navigate to="/policies/ecotour-admin-privacy-policy" replace />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
    </Routes>
  )
}

function PortalRouteShell() {
  const hydrating = useHomepageHydration()
  const affiliateAttributionEnabled = isAffiliateAttributionEnabled()
  const puriDemoEnabled = isPuriDemoEnabled()
  const location = useLocation()
  const deferredUiReady = useDeferredPortalUi()
  const routeGlobalsActivated = useDeferredDetailGlobals(location.pathname)
  const globalUiReady = deferredUiReady && routeGlobalsActivated
  const isEmbedHostedFormRoute = location.pathname.startsWith('/embed/forms/')
  const isDemoRoute = puriDemoEnabled && isPuriDemoPath(location.pathname)
  const isExploreHomeRoute = location.pathname === HOME_LANDING_ROUTES.explore || location.pathname === '/explore'
  const listingFamily = resolvePublicListingFamily(location.pathname)
  const routeContent = (
    <PublicListingImageBudgetProvider
      family={listingFamily}
      routeKey={`${location.pathname}${location.search}`}
    >
      {isHomeLandingPath(location.pathname) ? <PortalRoutes deferredUiReady={globalUiReady} /> : (
        <React.Suspense fallback={<RouteLoadingFallback pathname={location.pathname} />}>
          <PortalRoutes deferredUiReady={globalUiReady} />
        </React.Suspense>
      )}
    </PublicListingImageBudgetProvider>
  )

  if (isDemoRoute) {
    return (
      <>
        <a className="ecotour-skip-link" href="#main-content">Skip to main content</a>
        <RouteScrollRestoration />
        <div id="main-content" tabIndex={-1}>{routeContent}</div>
      </>
    )
  }

  return (
    <QueryProvider enabled={routeGlobalsActivated}>
      <WebsiteConfigProvider enabled={routeGlobalsActivated}>
        <SupportDeskProvider>
          <WebsitePromotionsProvider enabled={routeGlobalsActivated}>
            <a className="ecotour-skip-link" href="#main-content">Skip to main content</a>
            <RouteScrollRestoration />
            <PublicRoutePrefetcher />
            <LegacyStyleRouteBootstrapper />
            <PortalPerformanceMonitor />
            <PortalVisitorTracker enabled={routeGlobalsActivated} />
            {routeGlobalsActivated ? <UserBookingsBootstrapper /> : null}
            {affiliateAttributionEnabled && !isEmbedHostedFormRoute ? <AffiliateAttributionBootstrapper /> : null}
            {isEmbedHostedFormRoute || !routeGlobalsActivated ? null : <CartBootstrapper />}
            {isEmbedHostedFormRoute ? null : <BackNavigationGuard />}
            <div id="main-content" tabIndex={-1}>{routeContent}</div>
            {hydrating ? null : <QuickBookRouteEntry />}
            {globalUiReady && !isEmbedHostedFormRoute ? (
              <React.Suspense fallback={null}>
                <WebsitePromotionsViewport />
                <AssistanceEntrySurfaces />
                {isExploreHomeRoute ? null : <ExploreQuickRoutesBar />}
                <Footer />
              </React.Suspense>
            ) : null}
            {isEmbedHostedFormRoute || hydrating ? null : <TestSiteAccessGate />}
            {isEmbedHostedFormRoute || hydrating ? null : <TestEnvironmentOverlay />}
          </WebsitePromotionsProvider>
        </SupportDeskProvider>
      </WebsiteConfigProvider>
    </QueryProvider>
  )
}

function AssistanceEntrySurfaces() {
  const { supportRollout } = useWebsiteConfig()
  const surfaces = resolveAssistanceSurfaces({
    conciergeEnabled: AI_CONCIERGE_ENABLED,
    helpHubEnabled: AI_CONCIERGE_HELP_HUB_ENABLED,
    supportFloatingEnabled: isSupportEntryEnabled(supportRollout, 'floating_widget'),
  })

  return (
    <>
      <SupportDeskDrawer conciergeEntryEnabled={surfaces.conciergeEntryEnabled} />
      {surfaces.standaloneConciergeEnabled && EcoTourConcierge ? <EcoTourConcierge /> : null}
    </>
  )
}

function RouterPage({ initialPath }) {
  const RouteProvider = typeof window === 'undefined' ? MemoryRouter : Router
  return (
    <div>
      <RouteProvider initialEntries={initialPath ? [initialPath] : undefined}>
        <PortalRouteShell />
      </RouteProvider>
    </div>
  )
}

export default RouterPage
